import hash from 'object-hash'
import PropTypes from 'prop-types'
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState
} from 'react'
import MainContainerLayout from 'src/components/MainContainerLayout/MainContainerLayout'
import Typography from 'src/ui-kit/components/Typography'
import { getNodeText } from 'src/utils/getNodeText'

import {
  OVOFootnoteWrapper,
  OVOFootnoteContainer,
  OVOFootnoteTitle,
  OVOFootnoteListItem,
  OVOFootnoteList,
  OVOFootnoteReferenceMarker,
  OVOFootnoteFootnoteMarker
} from './OVOFootnoteStyles'

const OVOPropTypesChildren = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node
])

const OVOPropTypesStyle = PropTypes.oneOfType([PropTypes.object, PropTypes.array])

const OVOFootnoteMarkerTypePropTypes = PropTypes.oneOf(['cross', 'asterix', 'number', 'adaptive'])

type ReferencedItem = {
  children?: React.ReactNode
  id?: string
  ref?: React.RefObject<Element>
  content?: { id: string }
}

const OVOFootnoteFactory = () => {
  const defaultContext: {
    ctxKey: string | undefined
    markerType: string
    referenced: ReferencedItem[]
    setReferenced: Dispatch<SetStateAction<ReferencedItem[]>>
  } = {
    ctxKey: undefined,
    markerType: 'number',
    referenced: [],
    setReferenced: () => {
      console.error('No FootnoteProvider was found.')
    }
  }
  const Context = createContext(defaultContext)

  const Provider = ({
    children,
    markerType = 'number',
    contextKey,
  }: {
    children?: React.ReactNode
    markerType?: string
    contextKey?: string
  }) => {
    // For test purposes used ability to wrap providers to re-use parent's ctxKey
    const usedContext = React.useContext(Context)
    const generatedCtxKey = useId().replace(/:/g, '')
    const ctxKey = contextKey || usedContext.ctxKey || generatedCtxKey
    const [referenced, setReferenced] = useState<ReferencedItem[]>([])
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const elements = ref.current ? Array.from( ref.current.querySelectorAll( `sup[data-role="footnote-reference"][data-ctx="${ctxKey}"]` ) ) : []
      setReferenced(state => {
        const sortedState = elements.reduce<ReferencedItem[]>(
          (result, element) => {
            const referencedItem = state.find(item =>
              item.ref && item.ref.current === element &&
                  result.findIndex(resultItem => item.content?.id === resultItem.content?.id) < 0)
            if (referencedItem) {
              result.push(referencedItem)
            }
            return result
          },
          []
        )

        const isAlreadySorted = !sortedState
          .filter((item, index) => state[index]?.ref?.current !== item.ref?.current)
          .length

        if (isAlreadySorted) {
          return state
        }

        return sortedState
      })
    }, [referenced])

    return (
      <Context.Provider
        value={{
          markerType,
          referenced,
          setReferenced,
          ctxKey
        }}
      >
        <div ref={ref} data-role="footnote-provider" data-ctx={ctxKey}>
          {children}
        </div>
      </Context.Provider>
    )
  }

  Provider.propTypes = {
    markerType: OVOFootnoteMarkerTypePropTypes,
    children: OVOPropTypesChildren.isRequired,
    contextKey: PropTypes.string
  }

  const YieldMaker = ({
    markerIndex,
    className,
    children,
    style = {},
    ['data-footnote-id']: footnoteId
  }: {
    markerIndex: number
    className?: string
    children?: React.ReactNode
    style?: React.CSSProperties
    'data-footnote-id'?: string
  }) => {
    const { ctxKey, markerType, referenced } = useContext(Context)
    const marker =
        (markerType === 'asterix' && '*'.repeat(markerIndex)) ||
        (markerType === 'adaptive' &&
          ((referenced.length > 3 && markerIndex) || '*'.repeat(markerIndex))) ||
        markerIndex
    const Marker = footnoteId && OVOFootnoteReferenceMarker || OVOFootnoteFootnoteMarker
    return (
      <>
        <Marker
          data-role={(footnoteId && 'footnote-reference') || ''}
          data-ctx={ctxKey}
          className={className}
          style={style}
          data-footnote-id={footnoteId}
        >
          [{marker}]
        </Marker>
        {children}
      </>
    )
  }

  YieldMaker.propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ]),
    markerIndex: PropTypes.number.isRequired,
    className: PropTypes.string,
    style: OVOPropTypesStyle,
    'data-footnote-id': PropTypes.string
  }

  const Referenced = ({
    children,
    style = {},
  }: {
    children?: React.ReactNode
    style?: React.CSSProperties
  }) => {

    const { referenced, setReferenced } = useContext(Context)
    const [id] = useState(hash(getNodeText(children) ?? ''))

    useEffect(() => {
      const referencedItem = { children, id }
      setReferenced && setReferenced(state => [...state, referencedItem])
      return () => {
        setReferenced && setReferenced(state => state.filter(item => item !== referencedItem))
      }
    }, [])
    const referencedItems = referenced.filter(({ id: id1 }, index) =>
      referenced.findIndex(({ id: id2 }) => id1 === id2) === index)

    const referencedIndex = referencedItems.findIndex(item => (item.id === id)) + 1

    return (
      <YieldMaker
        markerIndex={referencedIndex}
        data-footnote-id={id}
        style={style}
      />
    )
  }
  Referenced.propTypes = {
    children: PropTypes.node,
    style: OVOPropTypesStyle
  }

  const Yield = ({
    isEnabled = true,
    isFullWidth = false,
    footnotesHeading = 'Things you need to know',
    customFootnoteWrapperStyle = {},
  }: {
    isEnabled?: boolean
    isFullWidth?: boolean
    footnotesHeading?: React.ReactNode | false
    customFootnoteWrapperStyle?: React.CSSProperties
  }) => {
    const { ctxKey, referenced } = useContext(Context)
    const referencedItems = referenced.filter(({ id: id1 }, index) =>
      referenced.findIndex(({ id: id2 }) => id1 === id2) === index)
    return (
      isEnabled && <OVOFootnoteWrapper>
        <MainContainerLayout>
          <OVOFootnoteContainer isFullWidth={isFullWidth}>
            {footnotesHeading && <OVOFootnoteTitle>
              <Typography variant="h4">{footnotesHeading}</Typography>
            </OVOFootnoteTitle>}
            <OVOFootnoteList data-role="footnote-referenced-list" data-ctx={ctxKey}>
              {referencedItems.map(({ id, children }, index) => (
                <OVOFootnoteListItem
                  key={id}
                  className="footnote-referenced-item"
                  data-testid={`footnote-yield-${index + 1}`}
                  style={customFootnoteWrapperStyle}
                  data-ctx={ctxKey}
                >
                  <YieldMaker markerIndex={index + 1}/> {children}
                </OVOFootnoteListItem>
              ))}
            </OVOFootnoteList>
          </OVOFootnoteContainer>
        </MainContainerLayout>
      </OVOFootnoteWrapper> || null
    )
  }
  Yield.propTypes = {
    footnotesHeading: PropTypes.oneOf([PropTypes.element, PropTypes.string, false]),
    customFootnoteWrapperStyle: OVOPropTypesStyle,
  }

  return {
    OVOFootnoteProvider: Provider,
    OVOFootnoteReferenced: Referenced,
    OVOFootnoteYield: Yield,
  }
}

export const {
  OVOFootnoteProvider,
  OVOFootnoteReferenced,
  OVOFootnoteYield
} = OVOFootnoteFactory()

